import Head from 'next/head';
import styles from '../styles/Reports.module.scss';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Booking = DBSchema.Booking;

import { FormItem } from '../components/FormItem';
import { DateRangeInput, DateInput } from '../components/DateInput';
import {
  BasicCheckbox,
  BasicDropDown,
  BasicInput,
  ControlledCheckbox,
  ControlledDropDown,
  ControlledTextbox,
} from '../components/FormComponents';
import {
  Button,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getBookingByID,
  getBookingByVTUID,
  getFormSuggestions,
  getTracks,
} from '../lib/bookingQueries';
import { DateTime } from 'luxon';
import { getEmptyBooking } from '../lib/getEmptyBooking';
import getConfig from '../src/config/config';
import { DateRange } from '@mui/lab';
import BookingStatus = DBSchema.BookingStatus;
import { useSuggestions } from '../src/components/CheckinsPage/hooks';
import { Config } from '../src/config/config';

export async function getServerSideProps(context): Promise<any> {
  const config = getConfig();

  //   console.log("Site Prefix:", config.sitePrefix);

  const { id, vtuid } = context.query;
  const data = await getFormSuggestions();
  const allTracks = await getTracks();

  //   console.log("All Tracks:", allTracks);

  const tracks: string[] = allTracks
    .filter((track) => track.trackprefix != null)
    .map((track) => track.trackprefix);

  //   console.log("Tracks Before Filtering:", tracks);

  //   const sitePrefix = config.sitePrefix.toLowerCase();
  const sitePrefix =
    typeof window !== 'undefined'
      ? window.__ENV__?.NEXT_PUBLIC_SITE_PREFIX.toLowerCase() ?? 'site'
      : 'SITE';

  // Filter out the "PrefixSite" variant but keep the main site prefix
  const filteredTracks = tracks.filter((track) => track !== `${config.sitePrefix}Site`);

  //   console.log("Filtered Tracks:", filteredTracks);

  const bookings: Booking[] = id ? await getBookingByID(id) : await getBookingByVTUID(vtuid);

  const booking: Booking = JSON.parse(JSON.stringify(bookings))[0] ?? getEmptyBooking(vtuid ?? '');
  //@ts-ignore
  if (typeof booking.tracks === 'string') {
    booking.tracks = JSON.parse(booking.tracks);
  }

  if (!Array.isArray(booking?.tracks) && booking) booking.tracks = [];

  return {
    props: {
      suggestions: data,
      tracks: filteredTracks,
      booking,
      vtuid: vtuid ?? '',
      config,
    },
  };
}

interface BookingPageProps {
  suggestions: any;
  tracks: string[];
  booking: Booking;
  vtuid?: string;
  callback?: Function;
  config: Config;
  action?: string;
  limitEditing?: boolean;
  isReplay?: boolean;
}

const BookingPage = (props: BookingPageProps) => {
  //   console.log(props);
  const useTime =
    typeof window !== 'undefined'
      ? window.__ENV__?.NEXT_PUBLIC_CHECKINS_USE_TIME === 'true'
      : false;
  return (
    <div>
      <Head>
        <title>Booking</title>
      </Head>

      <main className={styles.main} style={{ padding: 0 }}>
        <BookingForm {...props} />
      </main>
    </div>
  );
};

export function BookingForm(props: BookingPageProps) {
  const { tracks, limitEditing, isReplay, config } = props;
  const {
    checkinsLabels,
    mandatoryCheckinsFields,
    internalMandatoryFields,
    hiddenBookingFields,
    bookingRequireDriverSurname,
  } = config;

  const suggestions = useSuggestions(props.suggestions);

  // Check if a field should be hidden
  const isFieldHidden = (fieldName: string): boolean => {
    return hiddenBookingFields?.includes(fieldName) || false;
  };

  // Validate driver surname
  const isDriverValid = (driver: any): boolean => {
    if (!bookingRequireDriverSurname) return true;
    if (!driver) return false;

    // Handle both string and object (from "Add new" dropdown option)
    let driverString: string;
    if (typeof driver === 'string') {
      driverString = driver;
    } else if (typeof driver === 'object' && driver !== null && typeof driver.inputValue === 'string') {
      driverString = driver.inputValue;
    } else {
      return false;
    }

    // Check if driver contains a space followed by at least one character
    const parts = driverString.trim().split(/\s+/);
    return parts.length >= 2 && parts[1].length >= 1;
  };

  // Check if value is numeric
  const isNumericValue = (value: any): boolean => {
    return /^\d+$/.test(String(value));
  };

  const [dateRange, setDateRange] = useState<DateRange<DateTime>>([
    DateTime.fromISO(props?.booking?.startdate),
    DateTime.fromISO(props?.booking?.enddate),
  ]);
  const [selectedTracks, setSelectedTracks] = useState([...(props.booking.tracks ?? [])]);
  const [isAnyTrackSelected, setIsAnyTrackSelected] = useState(
    props.booking.internal ||
      (Array.isArray(props.booking.tracks) && props.booking.tracks.length > 0)
  );

  const [id, setID] = useState<string | number>(props.booking?.id ?? 0);
  const [status, setStatus] = useState(props.booking.status);

  const [allowSubmit, setAllowSubmit] = useState(true);
  const [formValues, setFormValues] = useState<Booking>({
    ...props.booking,
    vtuid: props.vtuid ?? props.booking?.vtuid,
  });
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [endDate, setEndDate] = useState(DateTime.fromISO(props?.booking?.enddate));
  const [startDate, setStartDate] = useState(DateTime.fromISO(props?.booking?.startdate));

  const useTime = config.useTime;

  const ref: React.MutableRefObject<HTMLFormElement | null> = useRef(null);

  const statusBasedRequiredFields = useMemo(() => getRequiredFormFields(status), [status]);

  const finalRequiredFields = useMemo(() => {
    const defaultRequiredShape: Required = {
      // Ensures all keys are present and typed
      tracks: false,
      bookingid: false,
      driver: false,
      vtuid: false,
      vehiclecategory: false,
      company: false,
      account: false,
      department: false,
      test: false,
      regno: false,
      bookedby: false,
      tlevel: false,
      radio_number: false,
      phone_number: false,
    };

    let result: Required;

    // For internal bookings, use internalMandatoryFields
    if (props.booking.internal) {
      if (internalMandatoryFields && internalMandatoryFields.length > 0) {
        const fromEnv: Partial<Required> = {};
        for (const field of internalMandatoryFields) {
          if (field in defaultRequiredShape) {
            fromEnv[field as keyof Required] = true;
          }
        }
        result = { ...defaultRequiredShape, ...fromEnv };
      } else {
        // Default internal booking requirements
        result = { ...defaultRequiredShape };
        result.vtuid = true;
        result.vehiclecategory = true;
      }
    } else {
      // For non-internal bookings, use mandatoryCheckinsFields or status-based logic
      if (mandatoryCheckinsFields && mandatoryCheckinsFields.length > 0) {
        // If env var is set, it's the SOLE source of truth
        const fromEnv: Partial<Required> = {};
        for (const field of mandatoryCheckinsFields) {
          if (field in defaultRequiredShape) {
            // Check if field is a valid key of Required
            fromEnv[field as keyof Required] = true;
          }
        }
        result = { ...defaultRequiredShape, ...fromEnv };
      } else {
        // If env var is NOT set, use status-based logic, but ensure all keys from defaultShape are present
        result = { ...defaultRequiredShape, ...statusBasedRequiredFields };
      }
    }

    return result;
  }, [statusBasedRequiredFields, mandatoryCheckinsFields, internalMandatoryFields, status, props.booking.internal]); // added props.booking.internal to deps

  const requiredStatus: Record<keyof Required, boolean> = useMemo(() => {
    return Object.fromEntries(
      Object.entries(finalRequiredFields).map(([key, isActuallyRequired]) => {
        if (!ref.current) return [key, false];

        const K = key as keyof Booking;
        const valueInForm = formValues[K];

        if (K === 'tracks') {
          return [
            key,
            isActuallyRequired ? Array.isArray(valueInForm) && valueInForm.length > 0 : true,
          ];
        }

        // Special validation for driver field when surname is required
        if (K === 'driver' && bookingRequireDriverSurname && finalRequiredFields.driver) {
          let driverValue: string | null = null;

          // Handle both string and object (from "Add new" dropdown option)
          if (typeof valueInForm === 'string') {
            driverValue = valueInForm;
          } else if (
            typeof valueInForm === 'object' &&
            valueInForm !== null &&
            typeof (valueInForm as any).inputValue === 'string'
          ) {
            driverValue = (valueInForm as any).inputValue;
          }

          if (!driverValue) {
            return [key, false];
          }

          return [key, driverValue.trim() !== '' && isDriverValid(driverValue)];
        }

        // Special validation for numeric fields
        if ((K === 'radio_number' || K === 'phone_number') && isActuallyRequired) {
          const value = valueInForm as string;
          if (!value || value.trim() === '') {
            return [key, false]; // Empty, not valid
          }
          return [key, isNumericValue(value)]; // Must be numeric
        }

        let isFilled = false;
        if (typeof valueInForm === 'string') {
          isFilled = valueInForm.trim() !== '';
        } else if (typeof valueInForm === 'number') {
          isFilled = true;
        } else if (typeof valueInForm === 'boolean') {
          isFilled = true;
        } else if (Array.isArray(valueInForm)) {
          isFilled = valueInForm.length > 0;
        } else if (
          typeof valueInForm === 'object' &&
          valueInForm !== null &&
          typeof (valueInForm as any).inputValue === 'string'
        ) {
          // Handles ControlledDropDown values when "Add new" is used, which result in { inputValue: string, label: string }
          isFilled = (valueInForm as any).inputValue.trim() !== '';
        } else if (typeof valueInForm === 'object' && valueInForm !== null) {
          // Fallback for other non-null objects, consider them filled if they exist.
          // This might be too broad if other complex objects can be "empty".
          // However, for simple selected objects from a dropdown that are not strings, this might suffice.
          isFilled = true;
        }

        return [key, isActuallyRequired ? isFilled : true];
      })
    ) as Record<keyof Required, boolean>;
  }, [finalRequiredFields, formValues, status, bookingRequireDriverSurname]);

  // DEBUG LOGS
  // useEffect(() => {
  //   console.log("[Debug] finalRequiredFields:", finalRequiredFields);
  //   console.log("[Debug] requiredStatus:", requiredStatus);
  //   console.log("[Debug] formValues:", formValues);
  // }, [finalRequiredFields, requiredStatus, formValues]);
  // END DEBUG LOGS

  useEffect(() => {
    const okay = !Object.values(requiredStatus).some((val) => !val);
    console.log("[Debug] Calculated 'okay' for allowSubmit:", okay);
    setAllowSubmit(okay);
  }, [requiredStatus, finalRequiredFields, formValues]); // Added finalRequiredFields and formValues as they indirectly influence this via requiredStatus

  const categories = props.booking.internal
    ? suggestions.categories.internal
    : suggestions.categories.nonInternal;
  const updateValue = (name, val) => setFormValues((vals) => ({ ...vals, [name]: val }));

  useEffect(() => {
    updateValue('status', status);
  }, [status]);

  useEffect(() => {
    setFormValues(props.booking);
  }, [JSON.stringify(props.booking)]);

  useEffect(() => {
    setIsAnyTrackSelected(props.booking.internal || selectedTracks.length > 0);
  }, [selectedTracks, props.booking.internal]);

  const showErrors = submitAttempted && !allowSubmit;
  const displayTMSID = props.booking.id ?? 0 > 0 ? `${props.booking.id}` : 'N/A';

  const statusOptions: BookingStatus[] = getStatusOptions(props.booking);

  function handleForm(e) {
    e.preventDefault();
    console.log('[handleForm] Clicked Submit');
    setSubmitAttempted(true);

    const currentForm = ref.current;

    const isFormInternallyValid = currentForm?.checkValidity();
    console.log('[handleForm] currentForm?.checkValidity():', isFormInternallyValid);
    console.log('[handleForm] allowSubmit state:', allowSubmit);

    if (!currentForm || !isFormInternallyValid || !allowSubmit) {
      console.log(
        '[handleForm] Returning early. currentForm is null, or form not internally valid, or fields not allowed.'
      );
      console.log('[handleForm] currentForm exists:', !!currentForm);
      return;
    }

    setLoading(true);
    console.log('[handleForm] Proceeding to build FormData and fetch.');
    const rawFormData = new FormData(currentForm);

    if (!useTime) {
      if (dateRange[0] && dateRange[0].isValid) {
        rawFormData.set('startdate', dateRange[0].toUTC().toISO());
      } else {
        rawFormData.delete('startdate');
      }
      if (dateRange[1] && dateRange[1].isValid) {
        rawFormData.set(
          'enddate',
          dateRange[1].set({ hour: 23, minute: 59, second: 59 }).toUTC().toISO()
        );
      } else {
        rawFormData.delete('enddate');
      }
    } else {
      if (startDate && startDate.isValid) {
        rawFormData.set('startdate', startDate.set({ second: 0 }).toUTC().toISO());
      } else {
        rawFormData.delete('startdate');
      }
      if (endDate && endDate.isValid) {
        rawFormData.set('enddate', endDate.set({ second: 0 }).plus({ minute: 30 }).toUTC().toISO());
      } else {
        rawFormData.delete('enddate');
      }
    }

    const parsedTracks = JSON.stringify(
      [...rawFormData.entries()].filter(([name]) => tracks.includes(name)).map(([name]) => name)
    );
    const formDataEntries = [
      ...[...rawFormData.entries()].filter(([name]) => !tracks.includes(name)),
      ['tracks', parsedTracks],
    ];

    const formDataBadCheckboxes = Object.fromEntries(formDataEntries);

    const formData = {
      ...props.booking,
      ...formDataBadCheckboxes,
      alarms: formDataBadCheckboxes['alarms'] == 'on',
      exclusive: formDataBadCheckboxes['exclusive'] == 'on',
      status: status,
    };
    console.log('[handleForm] Submitting formData:', formData);
    fetch('/api/booking/updateBooking', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((res) => {
        console.log('[handleForm] Fetch response status:', res.status);
        console.log('[handleForm] Fetch response:', res);
        setLoading(false);

        if (res?.status == 200) {
          setSuccess(true);
          fetch('/api/alarms/doUpdate').catch(console.error);
          window.parent.postMessage('done', window.parent.location.origin);
          if (props.callback) {
            props.callback(res);
          }
          setTimeout(() => {
            setSuccess(false);
          }, 2000);

          res
            .json()
            .then((data) => {
              console.log('[handleForm] Parsed JSON data:', data);
              if (typeof id === 'number' && id > 0) {
                setID(data.id);
              }
            })
            .catch((err) => {
              console.error('[handleForm] Error parsing JSON response:', err);
            });
        } else {
          // Handle error response
          res
            .json()
            .then((data) => {
              console.error('[handleForm] Server error:', data);
              alert(`Failed to save booking: ${data.error || 'Unknown error'}`);
            })
            .catch((err) => {
              console.error('[handleForm] Error parsing error response:', err);
              alert(`Failed to save booking. Status: ${res.status}`);
            });
        }
      })
      .catch((err) => {
        console.error('[handleForm] Fetch error:', err);
        setLoading(false);
        alert(`Network error: Unable to save booking. ${err.message}`);
      });
  }

  return (
    <>
      <form
        ref={ref}
        className={styles.bookingForm}
        action="/api/booking/updateBooking"
        onSubmit={(e) => {
          e.preventDefault();
        }}
      >
        <Grid>
          <Grid container item justifyContent="center">
            {!props.booking.internal && (
              <SidePanel
                tracks={props.tracks.filter(
                  (track) => track !== config.sitePrefix && track !== `${config.sitePrefix}Site`
                )}
                vtuid={props.booking?.vtuid ?? props.vtuid}
                limitEditing={limitEditing}
                element={(track) => (
                  <ControlledCheckbox
                    value={selectedTracks.includes(track)}
                    setValue={(val) => {
                      if (selectedTracks.includes(track)) {
                        setSelectedTracks((tracks) => tracks.filter((item) => item !== track));
                      } else {
                        setSelectedTracks((tracks) => [...tracks, track]);
                      }
                    }}
                    key={track}
                    name={track}
                    label={track}
                  />
                )}
                selectAllTracks={(val) => {
                  setSelectedTracks(() =>
                    val
                      ? [
                          ...props.tracks.filter(
                            (track) =>
                              track !== config.sitePrefix && track !== `${config.sitePrefix}Site`
                          ),
                        ]
                      : []
                  );
                }}
                booking={props.booking}
                dateProps={{
                  dateRange,
                  setDateRange,
                  startDate,
                  endDate,
                  setStartDate,
                  setEndDate,
                  useTime,
                }}
                action={props.action}
                selectedTracks={selectedTracks}
                config={config}
              />
            )}
            <Stack>
              <input name={'id'} hidden value={id} onChange={(e) => setID(e.target.value)} />
              {!props.booking.internal && !isFieldHidden('bookingid') && (
                <ControlledDropDown
                  name="bookingid"
                  disabled={limitEditing}
                  defaultValue={props.booking.bookingid}
                  options={[
                    ...suggestions.bookingIDs
                      .map((id) => id.bookingid)
                      .filter((id) => id.length > 0)
                      .sort(),
                  ]}
                  required={finalRequiredFields.bookingid}
                  value={formValues.bookingid}
                  updateValue={updateValue}
                  helperText={
                    showErrors && !requiredStatus.bookingid ? 'This field is requried' : undefined
                  }
                  label={checkinsLabels.bookingId}
                />
              )}
              {!isFieldHidden('vtuid') && (
                <ControlledDropDown
                  name="vtuid"
                disabled={limitEditing || status === 'Closed'}
                options={[
                  ...suggestions.trackers
                    .map((tracker) => tracker.vtuid)
                    .filter((id) => id.length > 0)
                    .sort(),
                ]}
                disableCustom
                required={finalRequiredFields.vtuid}
                helperText={
                  showErrors && !requiredStatus.vtuid ? 'This field is required' : undefined
                }
                value={formValues.vtuid}
                updateValue={updateValue}
                label={checkinsLabels.vtuId}
                />
              )}
              {!isFieldHidden('company') && (
                <ControlledDropDown
                  name={'company'}
                disabled={limitEditing}
                value={formValues.company}
                updateValue={updateValue}
                options={[...suggestions.companies.map((company) => company.company)]}
                required={finalRequiredFields.company}
                helperText={
                  showErrors && !requiredStatus.company ? 'This field is required' : undefined
                }
                label={checkinsLabels.company}
                />
              )}
              {!props.booking.internal && !isFieldHidden('driver') && (
                <ControlledDropDown
                  disabled={limitEditing}
                  options={suggestions.drivers
                    .map((driver) => driver.driver)
                    .filter((name) => name.length > 1)}
                  value={formValues.driver}
                  updateValue={updateValue}
                  required={finalRequiredFields.driver}
                  helperText={
                    showErrors && !requiredStatus.driver
                      ? bookingRequireDriverSurname &&
                        formValues.driver &&
                        !isDriverValid(formValues.driver)
                        ? 'Include surname'
                        : 'This field is required'
                      : undefined
                  }
                  name="driver"
                  label={checkinsLabels.driver}
                />
              )}
              {!isFieldHidden('account') && (
                <ControlledDropDown
                  name={'account'}
                  disabled={limitEditing}
                  value={formValues.account}
                  updateValue={updateValue}
                  options={[...suggestions.accounts.map((account) => account.account)]}
                  label={checkinsLabels.account}
                  required={finalRequiredFields.account}
                  helperText={
                    showErrors && !requiredStatus.account ? 'This field is required' : undefined
                  }
                />
              )}
              {!isFieldHidden('department') && (
                <ControlledDropDown
                  name="department"
                  value={formValues.department}
                  updateValue={updateValue}
                  disabled={limitEditing}
                  options={suggestions.departments
                    .map((dep) => dep.department)
                    .filter((dep) => dep.length > 0)}
                  label={checkinsLabels.department}
                  required={finalRequiredFields.department}
                  helperText={
                    showErrors && !requiredStatus.department ? 'This field is required' : undefined
                  }
                />
              )}
              {!isFieldHidden('radio_number') && (
                <ControlledTextbox
                  name="radio_number"
                  value={formValues.radio_number ?? null}
                  updateValue={updateValue}
                  disabled={limitEditing}
                  label="Radio Number"
                  type="text"
                  required={finalRequiredFields.radio_number}
                  helperText={
                    showErrors && !requiredStatus.radio_number
                      ? formValues.radio_number && !isNumericValue(formValues.radio_number)
                        ? 'Must be numeric value'
                        : 'This field is required'
                      : undefined
                  }
                />
              )}
              {!isFieldHidden('phone_number') && (
                <ControlledTextbox
                  name="phone_number"
                  value={formValues.phone_number ?? null}
                  updateValue={updateValue}
                  disabled={limitEditing}
                  label="Phone Number"
                  type="text"
                  required={finalRequiredFields.phone_number}
                  helperText={
                    showErrors && !requiredStatus.phone_number
                      ? formValues.phone_number && !isNumericValue(formValues.phone_number)
                        ? 'Must be numeric value'
                        : 'This field is required'
                      : undefined
                  }
                />
              )}
              {!props.booking.internal && !isFieldHidden('test') && (
                <ControlledDropDown
                  value={formValues.test}
                  updateValue={updateValue}
                  disabled={limitEditing}
                  options={suggestions.tests
                    .map((test) => test.test)
                    .filter((test) => test.length > 1)}
                  defaultValue={props.booking?.test}
                  hidden={props.booking.internal}
                  name="test"
                  label={checkinsLabels.test}
                  required={finalRequiredFields.test}
                  helperText={
                    showErrors && !requiredStatus.test ? 'This field is required' : undefined
                  }
                />
              )}
              {!isFieldHidden('bookedby') && (
                <ControlledDropDown
                  disabled={limitEditing}
                  value={formValues.bookedby}
                  updateValue={updateValue}
                  options={suggestions.bookedby
                    .map((bb) => bb.bookedby)
                    .filter((bb) => bb.length > 0)}
                  name="bookedby"
                  label={checkinsLabels.bookedBy}
                  required={finalRequiredFields.bookedby}
                  helperText={
                    showErrors && !requiredStatus.bookedby ? 'This field is required' : undefined
                  }
                />
              )}
              {!isFieldHidden('regno') && (
                <ControlledTextbox
                  value={formValues.regno}
                  updateValue={updateValue}
                  name="regno"
                  disabled={limitEditing}
                  label={checkinsLabels.regNo}
                  required={finalRequiredFields.regno}
                  helperText={
                    showErrors && !requiredStatus.regno ? 'This field is required' : undefined
                  }
                />
              )}
              {!isFieldHidden('vehiclecategory') && (
                <ControlledDropDown
                  name={'vehiclecategory'}
                label={checkinsLabels.vehicleCategory}
                defaultValue={props.booking?.vehiclecategory}
                disabled={limitEditing}
                disableCustom
                required={finalRequiredFields.vehiclecategory}
                value={formValues.vehiclecategory}
                updateValue={updateValue}
                helperText={
                  showErrors && !requiredStatus.vehiclecategory
                    ? 'This field is requried'
                    : undefined
                }
                options={[...categories]}
                />
              )}
              {!props.booking.internal && !isFieldHidden('tlevel') && (
                <BasicDropDown
                  defaultValue={props.booking?.tlevel ?? 'T1'}
                  name={'tlevel'}
                  label={checkinsLabels.tLevel}
                  disabled={limitEditing}
                  options={['T1', 'T2', 'T3']}
                  required={finalRequiredFields.tlevel}
                  disableCustom
                  hidden={props.booking.internal}
                />
              )}
              {!props.booking.internal && (
                <Grid item container>
                  <Grid item xs={8}>
                    <ControlledDropDown
                      name={'status'}
                      disabled={true}
                      value={status}
                      updateValue={(name, val: BookingStatus) => setStatus(val)}
                      options={statusOptions}
                      label={checkinsLabels.status}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <BasicDropDown
                      name={'TMS ID'}
                      defaultValue={displayTMSID}
                      options={[displayTMSID]}
                      disabled={true}
                      label={checkinsLabels.tmsId}
                    />
                  </Grid>
                </Grid>
              )}
            </Stack>
          </Grid>
          <Grid item container>
            <TextField
              fullWidth
              label={'Comments'}
              sx={{ marginX: 2, marginTop: 1 }}
              multiline
              name={'comments'}
              maxRows={3}
              minRows={1}
              defaultValue={props?.booking?.comments}
              disabled={limitEditing}
            />
          </Grid>
          <Grid item container justifyContent={'center'}>
            <FormItem>
              <div className={styles.submit} data-iframe-height="">
                <Button
                  sx={{ margin: 1 }}
                  color={'error'}
                  variant={'outlined'}
                  onClick={() => {
                    if (props.callback) {
                      props.callback();
                    }
                    window.parent.postMessage('done', window.parent.location.origin);
                  }}
                >
                  Cancel
                </Button>
                <Tooltip
                  title={
                    !formValues.bookingid &&
                    displayTMSID === 'N/A' &&
                    !formValues.internal &&
                    limitEditing
                      ? 'Please create a check-in on the check-ins page'
                      : !isAnyTrackSelected
                        ? 'Please select at least one track'
                        : ''
                  }
                >
                  <span>
                    <Button
                      sx={{ margin: 1, minWidth: '100px' }}
                      color={'success'}
                      variant={'contained'}
                      onClick={(e) => handleForm(e)}
                      onSubmit={(e) => {
                        e.preventDefault();
                      }}
                      disabled={
                        !!isReplay ||
                        !isAnyTrackSelected ||
                        (!formValues.bookingid &&
                          displayTMSID === 'N/A' &&
                          !formValues.internal &&
                          limitEditing)
                      }
                    >
                      {loading ? <CircularProgress color={'info'} size={24} /> : 'Submit'}
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </FormItem>
          </Grid>
        </Grid>
      </form>
    </>
  );
}

function SidePanel(props: {
  tracks: string[];
  element: (track: string) => JSX.Element;
  booking: Booking;
  vtuid: string;
  dateProps: {
    dateRange: DateRange<DateTime>;
    setDateRange: React.Dispatch<React.SetStateAction<DateRange<DateTime>>>;
    startDate: DateTime;
    endDate: DateTime;
    setStartDate: React.Dispatch<React.SetStateAction<DateTime>>;
    setEndDate: React.Dispatch<React.SetStateAction<DateTime>>;
    useTime: boolean;
  };
  action?: string;
  selectAllTracks: (state: boolean) => void;
  selectedTracks: string[];
  limitEditing?: boolean;
  config: Config;
}) {
  const trackHalf1 = props.tracks.filter((val, i, arr) => i < arr.length / 2);
  const trackHalf2 = props.tracks.filter((val) => !trackHalf1.includes(val));
  const { checkinsLabels } = props.config;

  const { dateRange, setDateRange, startDate, endDate, setStartDate, setEndDate, useTime } =
    props.dateProps;

  return (
    <Stack marginX={2} justifyContent={'space-around'}>
      <Grid
        container
        direction={'column'}
        justifyContent={'flex-start'}
        sx={{ marginTop: -1, marginBottom: -2 }}
      >
        {!!props?.action && (
          <Typography fontWeight={'bold'} variant={'h5'}>
            {!props.limitEditing && props.action}
          </Typography>
        )}
      </Grid>

      <Paper sx={{ padding: 1 }} elevation={3}>
        <Typography fontWeight={'bold'}>Booking Date</Typography>
        {!useTime && (
          <DateRangeInput
            name={'dateRange'}
            label={'Date Range'}
            range={dateRange}
            setRange={setDateRange}
          />
        )}
        {useTime && (
          <DateInput
            name={'startdate'}
            label={props.config.checkinsLabels.startDate}
            date={startDate}
            setDate={setStartDate}
          />
        )}
        {useTime && (
          <DateInput
            name={'enddate'}
            label={props.config.checkinsLabels.endDate}
            date={endDate}
            setDate={setEndDate}
          />
        )}
      </Paper>

      <Paper sx={{ paddingX: 2, paddingY: 0 }} elevation={3}>
        <Grid container justifyContent={'space-between'} alignItems={'center'}>
          <Typography fontWeight={'bold'}>Tracks</Typography>
          <ControlledCheckbox
            name={'selectAll'}
            value={props.selectedTracks.length === props.tracks.length}
            indeterminate={
              props.selectedTracks.length > 0 && props.selectedTracks.length < props.tracks.length
            }
            label={'Select All'}
            setValue={props.selectAllTracks}
          />
        </Grid>
        <Grid container>
          <Stack>{trackHalf1.map(props.element)}</Stack>
          <Stack>{trackHalf2.map(props.element)}</Stack>
        </Grid>
      </Paper>
      <Paper sx={{ paddingX: 2, paddingY: 0.5 }} elevation={3}>
        <Typography fontWeight={'bold'}>Options</Typography>
        <BasicCheckbox
          className={styles.bookingOption}
          name={'alarms'}
          label={'Select to enable Alarms'}
          defaultValue={props.booking?.alarms ?? true}
        />
        <BasicCheckbox
          className={styles.bookingOption}
          name={'exclusive'}
          label={'Select for Exclusive Check-in'}
          defaultValue={props.booking?.exclusive}
        />
      </Paper>
    </Stack>
  );
}

function getStatusOptions(booking: Booking): BookingStatus[] {
  if (!(booking.id ?? 0 > 0)) return ['Open', 'Active'];
  switch (booking.status) {
    case 'Closed':
      return ['Closed'];
  }
  return ['Open', 'Closed', 'Active', 'Expired'];
}

function getRequiredFormFields(status: BookingStatus): Partial<Required> {
  const open: Partial<Required> = {
    tracks: true,
    bookingid: true,
    driver: true,
    vtuid: false,
    vehiclecategory: false,
    company: true,
  };
  switch (status) {
    case 'Open':
      return open;
    case 'Active':
      return {
        ...open,
        vtuid: true,
        vehiclecategory: true,
      };
    default:
      return {
        tracks: false,
        bookingid: true,
        driver: false,
        vtuid: true,
        vehiclecategory: false,
        company: true,
        account: false,
        department: false,
        test: false,
        regno: false,
        bookedby: false,
        tlevel: false,
        radio_number: false,
        phone_number: false,
      };
  }
}

interface Required {
  tracks: boolean;
  bookingid: boolean;
  driver: boolean;
  vtuid: boolean;
  vehiclecategory: boolean;
  company: boolean;
  account: boolean;
  department: boolean;
  test: boolean;
  regno: boolean;
  bookedby: boolean;
  tlevel: boolean;
  radio_number: boolean;
  phone_number: boolean;
}

export default BookingPage;
